<template>
  <div
    class="group-accred-files-upload"
    data-qa="group-accred-files-upload">
    <slot name="title"/>
    <div class="group-accred-files-upload__content">
      <div class="group-accred-files-upload__description mb-4">
        {{ $t('groupOfAccreditation.filesUploadDescription') }}
      </div>
      <div
        v-for="group in groupsWithStatus"
        :key="`group-accred-upload-${group.id}`"
        class="group-accred-files-upload__item"
        :data-qa="`group-accred-upload-${group.organizationId}`">
        <div class="group-accred-files-upload__item-info">
          <router-link
            class="max-width-100 h3-headline"
            target="_blank"
            :to="{name: 'groupOfAccreditationsItem', params: { id: group.id, org: group.organizationItId }}">
            <TooltipedText
              :line-count="3"
              :value="groupValue.title"/>
          </router-link>
          <div class="group-accred-files-upload__item-title">
            {{ group.organization ? group.organization.title : `#${group.organizationId}` }}
          </div>
          <div
            v-if="group.organization && group.organization.oKPO"
            class="font-size-12 opacity-8">
            {{ $t('Tenders.EDRPOU') }}: {{ group.organization.oKPO }}
          </div>
        </div>
        <div>
          <sButton
            v-if="group.status === UPLOAD_STATUS.ERROR"
            class="ml-2"
            :data-qa="`group-accred-upload-retry-${group.organizationId}`"
            small
            type="text"
            @click="retryGroup(group.id)">
            {{ $t('groupOfAccreditation.retryUpload') }}
          </sButton>
        </div>
        <sChip
          :background="statusColors[group.status]"
          color="var(--color-black)"
          :data-qa="`group-accred-upload-status-${group.organizationId}`">
          {{ $t(`groupOfAccreditation.uploadStatus.${group.status}`) }}
        </sChip>
      </div>
    </div>
  </div>
</template>
<script>
import accreditationOrganizationIdMixin from 'mixins/accreditationOrganizationIdMixin.js'
import { mapGetters } from 'vuex'
import { StorageFile } from '@/types/commonFile.js'
import TooltipedText from 'components/common/blocks/tooltiped text/TooltipedText.vue'

export const UPLOAD_STATUS = {
  NOT_STARTED: 'NOT_STARTED',
  UPLOADING: 'UPLOADING',
  SUCCESS: 'SUCCESS',
  ERROR: 'ERROR',
}

export default {
  name: 'GroupOfAccreditationFilesUpload',
  mixins: [accreditationOrganizationIdMixin],
  data() {
    return {
      UPLOAD_STATUS,
      // { [groupId]: [статус кожного файлу з files за індексом] }
      fileStatuses: {},
      statusColors: {
        [UPLOAD_STATUS.NOT_STARTED]: 'var(--status-registration)',
        [UPLOAD_STATUS.UPLOADING]: 'var(--color-warning)',
        [UPLOAD_STATUS.SUCCESS]: 'var(--status-success)',
        [UPLOAD_STATUS.ERROR]: 'var(--status-decline)',
      },
    }
  },
  methods: {
    setFileStatus(groupId, fileIndex, status) {
      const statuses = [...(this.fileStatuses[groupId] || [])]
      statuses[fileIndex] = status
      this.$set(this.fileStatuses, groupId, statuses)
    },
    async uploadFile(groupId, fileIndex) {
      this.setFileStatus(groupId, fileIndex, UPLOAD_STATUS.UPLOADING)
      const storageFile = new StorageFile(groupId, this.files[fileIndex], 'Accreditation')
      try {
        const result = await storageFile.uploadFile()
        if (!result) {
          throw new Error(`File "${storageFile.title}" was not uploaded to accreditation group ${groupId}`)
        }
        this.setFileStatus(groupId, fileIndex, UPLOAD_STATUS.SUCCESS)
      } catch (error) {
        this.$log_error(error?.message || error)
        this.setFileStatus(groupId, fileIndex, UPLOAD_STATUS.ERROR)
      }
    },
    /**
     * Завантажує в групу файли, які ще не завантажені успішно (усі - при першому запуску, лише невдалі - при повторі)
     */
    uploadGroup(groupId) {
      const statuses = this.fileStatuses[groupId] || []
      return Promise.all(this.files
        .map((_, fileIndex) => fileIndex)
        .filter(fileIndex => statuses[fileIndex] !== UPLOAD_STATUS.SUCCESS)
        .map(fileIndex => this.uploadFile(groupId, fileIndex)))
    },
    retryGroup(groupId) {
      return this.uploadGroup(groupId)
    },
    async uploadAll() {
      // По організаціях послідовно, щоб не відправляти одночасно files.length * groups.length запитів
      for (const group of this.groups) {
        await this.uploadGroup(group.id)
      }
    },
    groupStatus(groupId) {
      const statuses = this.fileStatuses[groupId] || []
      if (statuses.includes(UPLOAD_STATUS.UPLOADING)) {
        return UPLOAD_STATUS.UPLOADING
      }
      if (statuses.includes(UPLOAD_STATUS.ERROR)) {
        return UPLOAD_STATUS.ERROR
      }
      if (this.files.every((_, fileIndex) => statuses[fileIndex] === UPLOAD_STATUS.SUCCESS)) {
        return UPLOAD_STATUS.SUCCESS
      }
      return UPLOAD_STATUS.NOT_STARTED
    },
  },
  computed: {
    ...mapGetters({
      availableOrganizations: 'getAvailableOrganizationsForCurrently',
    }),
    groupsWithStatus() {
      return this.groups.map(group => {
        const organization = (this.availableOrganizations || []).find(item => this.$_accreditationOrganizationKey(item) === group.organizationId)
        return {
          ...group,
          organization,
          // Параметр маршруту `org` завжди в просторі organizationItId (див. router.js, заголовок OrganizationItId)
          organizationItId: organization ? organization.organizationItId : (this.growthBook?.accreditationGroupsInternalOrganizationId ? undefined : group.organizationId),
          status: this.groupStatus(group.id),
        }
      })
    },
    isCompleted() {
      return this.groupsWithStatus.every(group => group.status === UPLOAD_STATUS.SUCCESS)
    },
  },
  watch: {
    isCompleted: {
      handler(value) {
        this.$emit('update:completed', value)
      },
      immediate: true,
    },
  },
  components: {
    TooltipedText,
  },
  mounted() {
    this.uploadAll()
  },
  props: {
    /** Створені групи: [{ id, organizationId }] */
    groups: {
      type: Array,
      required: true,
    },
    /** Файли (File) для завантаження в кожну групу */
    files: {
      type: Array,
      required: true,
    },
    /** Дані групи з форми (title тощо) - однакові для всіх створених груп */
    groupValue: {
      type: Object,
      required: true,
    },
  },
}
</script>
<style scoped lang="scss">
.group-accred-files-upload {
  max-width: 805px;
  margin: 0 auto;
  &__content {
    background-color: var(--v-main__light_green-base);
    border-radius: 8px;
    padding: 30px;
    text-align: left;
  }
  &__item {
    display: grid;
    grid-template-columns: 1fr auto auto;
    align-items: center;
    gap: 8px;
    padding: 12px 0;
    & + & {
      border-top: 1px solid var(--color-border, #D8F1F0);
    }
    &-title {
      font-weight: 500;
    }
  }
}
</style>
